import { useEffect, useRef, useState } from 'react';
import type { Crew } from '../net.js';

const CATEGORIES = [
  { id: 'bug', label: 'Something broke', hint: 'It did the wrong thing' },
  { id: 'confusing', label: 'This confused me', hint: 'I could not tell what to do' },
  { id: 'idea', label: 'I have an idea', hint: 'Something you could add' },
  { id: 'praise', label: 'This was good', hint: 'Worth keeping' }
] as const;

type Category = (typeof CATEGORIES)[number]['id'];

const MAX = 500;

export function Feedback({
  crew,
  onClose,
  context
}: {
  crew: Crew;
  onClose: () => void;
  context: { screen: string; missionId?: number; phase?: string };
}) {
  const [category, setCategory] = useState<Category>('bug');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const focusables = () =>
      Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), textarea, [href], input, [tabindex]:not([tabindex="-1"])'
        ) ?? []
      );

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0] as HTMLElement;
      const last = items[items.length - 1] as HTMLElement;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panelRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKey, true);
    inputRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const send = async () => {
    if (!message.trim() || busy) return;
    setBusy(true);
    const ok = await crew.sendFeedback({ category, message, ...context });
    setBusy(false);
    if (ok) {
      setSent(true);
      window.setTimeout(onClose, 1400);
    }
  };

  return (
    <div className="manual-backdrop" onClick={onClose}>
      <div
        className="feedback"
        role="dialog"
        aria-modal="true"
        aria-label="Send feedback"
        tabIndex={-1}
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
      >
        {sent ? (
          <div className="feedback__done" role="status">
            <span className="feedback__tick" aria-hidden="true">
              &#10003;
            </span>
            <h2>Thanks, got it</h2>
            <p>That goes straight into the log.</p>
          </div>
        ) : (
          <>
            <header className="feedback__head">
              <div>
                <p className="manual__eyebrow">Crew Feedback</p>
                <h2>How is it going?</h2>
              </div>
              <button className="btn btn--ghost btn--small" onClick={onClose}>
                Close
              </button>
            </header>

            <div className="feedback__body">
              <div className="feedback__cats" role="radiogroup" aria-label="Kind of feedback">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    role="radio"
                    aria-checked={category === c.id}
                    className={`feedback__cat ${category === c.id ? 'is-active' : ''}`}
                    onClick={() => setCategory(c.id)}
                  >
                    <strong>{c.label}</strong>
                    <small>{c.hint}</small>
                  </button>
                ))}
              </div>

              <label htmlFor="feedback-text">Tell us more</label>
              <textarea
                id="feedback-text"
                ref={inputRef}
                value={message}
                maxLength={MAX}
                rows={5}
                placeholder={
                  category === 'bug'
                    ? 'What happened, and what did you expect instead?'
                    : 'Say as much or as little as you like.'
                }
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void send();
                }}
              />

              <div className="feedback__foot">
                <span className={`feedback__count ${message.length > MAX - 60 ? 'is-near' : ''}`}>
                  {message.length}/{MAX}
                </span>
                <button
                  className="btn btn--primary"
                  onClick={() => void send()}
                  disabled={busy || !message.trim()}
                >
                  {busy ? 'Sending\u2026' : 'Send feedback'}
                </button>
              </div>

              <p className="feedback__privacy">
                Sent anonymously. Your name, your cards and the chat are never included. Just
                your message and which screen you were on.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
